<template>
  <canvas></canvas>
</template>

<script>
import viewMode from '../modules/viewMode.js';
import TimeAndTimelinePosition from './modules/TimeAndTimelinePosition.js';
import TimelineSegmentDomRectsToDragWithInfo from './modules/TimelineSegmentDomRectsToDragWithInfo.js';
import timelineCanvas from './mixins/timelineCanvas.js';
import canvasUtils from './modules/canvasUtils.js';
import Color from '../../../modules/Color.js';
import TimeValue from '../../../modules/TimeValue.js';
import { mouseButtonEnum } from '../../../modules/mouseEventUtils.js';
import TimelineMarker from '../../../modules/TimelineMarker.js';

const newSegmentBackgroundColor = new Color(207, 207, 207, 0.6);
const newSegmentBorderColor = new Color(130, 130, 130);
const cursorColor = new Color(0, 0, 0);
const segmentGhostBorderColor = new Color(50, 50, 50, 1);

export default {
  mixins: [
    timelineCanvas,
  ],

  watch: {
    verticalOffsetPx() { this.$_setDirty(true) },
    timeAndTimelinePositionAtMousedown: {
      handler() { this.$_setDirty(true) },
      deep: true,
    },
    timeAndTimelinePositionAtMousemove: {
      handler() { this.$_setDirty(true) },
      deep: true,
    },
    viewMode() { this.$_setDirty(true) },
    timelineViewBeginMin: {
      handler() { this.$_setDirty(true) },
      deep: true,
    },
    timelineViewEndMax: {
      handler() { this.$_setDirty(true) },
      deep: true,
    },
    timelineMarker: {
      handler() { this.$_setDirty(true) },
      deep: true,
    },
  },

  props: {
    timeAndTimelinePositionAtMousedown:    { type: TimeAndTimelinePosition },
    timeAndTimelinePositionAtMousemove:    { type: TimeAndTimelinePosition },
    isDragging:                            { type: Boolean },
    firstMouseButtonAtMousedown:           { type: String },
    isMousemoveOnTimelineCanvas:           { type: Boolean },
    isMousedownOnScrollBar:                { type: Boolean },
    isMousedownOnRulerArea:                { type: Boolean },
    timelineSegmentDomRectsToDragWithInfo: { type: TimelineSegmentDomRectsToDragWithInfo },
    segmentDragOffsetTime:                 { type: TimeValue },
    numVisibleTimelines:                   { type: Number },
    viewMode:                              { type: String },
    verticalOffsetPx:                      { type: Number },
    timelineHeightPx:                      { type: Number },
    minimumSegmentDuration:                { type: TimeValue },
    timelineViewBeginMin:                  { type: TimeValue },
    timelineViewEndMax:                    { type: TimeValue },
    timelineMarker:                        { type: TimelineMarker },
  },

  computed: {
    $_minimumSegmentDurationPx() {
      return this.scaledTimeToPx(this.minimumSegmentDuration);
    },

    $_segmentDragOffsetTimePx() {
      return this.scaledTimeToPx(this.segmentDragOffsetTime)
    },

    $_isViewModeCreate() {
      return (this.viewMode === viewMode.create);
    },

    $_snappedTimelineCanvasLocalXAtMousedown() {
      if (this.timeAndTimelinePositionAtMousedown === undefined) return undefined;
      return this.timeToTimelineCanvasLocalX(this.timeAndTimelinePositionAtMousedown.snappedTime);
    },

    $_timelineCanvasLocalYAtMousedown() {
      if (this.timeAndTimelinePositionAtMousedown === undefined) return undefined;
      return this.timelinePositionToTimelineCanvasLocalY(this.timeAndTimelinePositionAtMousedown.timelinePosition);
    },

    $_visibleTimelineDataIdxAtMousedown() {
      if (this.$_timelineCanvasLocalYAtMousedown === undefined) return undefined;
      return this.timelineCanvasLocalYToVisibleTimelineDataIdx(this.$_timelineCanvasLocalYAtMousedown);
    },

    $_isMousedownOnVisibleTimelineData() {
      return (this.$_visibleTimelineDataIdxAtMousedown !== undefined);
    },

    $_timelineCanvasLocalXNewSegmentBegin() {
      if (this.$_isViewModeCreate) {
        if (this.timelineMarker !== undefined) {
          if (this.timeAndTimelinePositionAtMousedown !== undefined) {
            if (this.timelineMarker.isInRange(this.timeAndTimelinePositionAtMousedown.time)) {
              if (this.$_isMousedownOnVisibleTimelineData) {
                let apparentTimelineMarkerBegin = (this.timelineMarker.begin === null)? this.timelineViewBeginMin : this.timelineMarker.begin;
                return this.timeToTimelineCanvasLocalX(apparentTimelineMarkerBegin);
              }
            }
          }
        }
      }
      return this.$_snappedTimelineCanvasLocalXAtMousedown;
    },

    $_snappedTimelineCanvasLocalXAtMousemove() {
      if (this.timeAndTimelinePositionAtMousemove === undefined) return undefined;
      return this.timeToTimelineCanvasLocalX(this.timeAndTimelinePositionAtMousemove.snappedTime);
    },

    $_timelineCanvasLocalYAtMousemove() {
      if (this.timeAndTimelinePositionAtMousemove === undefined) return undefined;
      return this.timelinePositionToTimelineCanvasLocalY(this.timeAndTimelinePositionAtMousemove.timelinePosition);
    },

    $_timelineCanvasLocalXNewSegmentEnd() {
      if (this.$_isViewModeCreate) {
        if (this.timelineMarker !== undefined) {
          if (this.timeAndTimelinePositionAtMousemove !== undefined) {
            if (this.timelineMarker.isInRange(this.timeAndTimelinePositionAtMousedown.time)) {
              if (this.timelineMarker.isInRange(this.timeAndTimelinePositionAtMousemove.time)) {
                if (this.$_isMousedownOnVisibleTimelineData) {
                  let apparentTimelineMarkerEnd = (this.timelineMarker.end === null)? this.timelineViewEndMax : this.timelineMarker.end;
                  return this.timeToTimelineCanvasLocalX(apparentTimelineMarkerEnd);
                }
              }
            }
          }
        }
      }
      return this.$_snappedTimelineCanvasLocalXAtMousemove;
    },
  },

  inject: [
    'scaledTimeToPx',
    'timeToTimelineCanvasLocalX',
    'timelinePositionToTimelineCanvasLocalY',
    'timelineCanvasLocalYToVisibleTimelineDataIdx',
  ],

  methods: {
    /* public */
    draw() {
      this.$_draw(() => {
        if (!this.isMousedownOnScrollBar && (this.isMousemoveOnTimelineCanvas || this.isDragging)) {
          drawCursorBar.call(this);
        }
        if (this.isDragging) {
          if (this.isMousedownOnRulerArea) {
            if (this.firstMouseButtonAtMousedown === mouseButtonEnum.main) {
              drawMarkerAreaGhost.call(this);
            }
          } else {
            switch (this.viewMode) {
            case viewMode.create:
              if (this.$_visibleTimelineDataIdxAtMousedown !== undefined) {
                if (this.firstMouseButtonAtMousedown === mouseButtonEnum.main) {
                  drawNewSegment.call(this, this.$_visibleTimelineDataIdxAtMousedown);
                }
              }
              break;
            default:
              if (this.timelineSegmentDomRectsToDragWithInfo !== undefined) {
                drawSegmentGhost.call(this);
              } else {
                drawDragArea.call(this);
              }
              break;
            }
          }
        }
      });

      function drawNewSegment(visibleTimelineDataIdx) {
        let timelineSegmentOffsetY = visibleTimelineDataIdx * this.timelineHeightPx + this.verticalOffsetPx;
        let timelineOverlayCanvas = this.$data.$_timelineCanvasContext;
        canvasUtils.drawRect(
          timelineOverlayCanvas,
          {
            x: this.$_timelineCanvasLocalXNewSegmentBegin,
            y: timelineSegmentOffsetY,
            width: this.$_timelineCanvasLocalXNewSegmentEnd - this.$_timelineCanvasLocalXNewSegmentBegin,
            height: this.timelineHeightPx,
          },
          {
            lineWidth: 1,
            lineDash: [ 4, 4 ],
            stroke: true,
            strokeStyle: newSegmentBorderColor.styleString,
            fill: true,
            fillStyle: newSegmentBackgroundColor.styleString,
          },
        );
      }

      function drawCursorBar() {
        let timelineOverlayCanvas = this.$data.$_timelineCanvasContext;
        canvasUtils.drawLine(
          timelineOverlayCanvas,
          { x: this.$_snappedTimelineCanvasLocalXAtMousemove, y: 0 },
          { y: this.$data.$_timelineCanvasHeightPx },
          { lineWidth: 1, strokeStyle: cursorColor.styleString, lineDash: [] },
        );
        timelineOverlayCanvas.font = 'normal 12px sans-serif';
        timelineOverlayCanvas.fillStyle = cursorColor.styleString;
        timelineOverlayCanvas.textAlign = 'left';
        timelineOverlayCanvas.textBaseline = 'middle';

        timelineOverlayCanvas.fillText(
          this.timeAndTimelinePositionAtMousemove.snappedTime.toFormatString(),
          this.$_snappedTimelineCanvasLocalXAtMousemove + 10,
          this.$_timelineCanvasLocalYAtMousemove,
        );
      }

      function drawMarkerAreaGhost() {
        let timelineOverlayCanvas = this.$data.$_timelineCanvasContext;
        canvasUtils.drawRect(
          timelineOverlayCanvas,
          {
            x: this.$_snappedTimelineCanvasLocalXAtMousedown,
            width: this.$_snappedTimelineCanvasLocalXAtMousemove - this.$_snappedTimelineCanvasLocalXAtMousedown,
            height: this.$data.$_timelineCanvasHeightPx,
          },
          {
            lineWidth: 1,
            lineDash: [ 4, 4 ],
            stroke: true,
            strokeStyle: newSegmentBorderColor.styleString,
          },
        );
      }

      function drawSegmentGhost() {
        let originalSegmentDomRects = this.timelineSegmentDomRectsToDragWithInfo.domRects;
        let endTypes = this.timelineSegmentDomRectsToDragWithInfo.endTypes;
        let edgeType = this.timelineSegmentDomRectsToDragWithInfo.edgeType;
        let numSegments = originalSegmentDomRects.length;
        for (let segmentIdx = 0; segmentIdx < numSegments; ++segmentIdx) {
          let originalSegmentDomRect = originalSegmentDomRects[segmentIdx];
          let endType = endTypes[segmentIdx];
          let x = undefined;
          let width = undefined;
          switch (edgeType) {
          case TimelineSegmentDomRectsToDragWithInfo.edgeType.right:
            x = originalSegmentDomRect.x;
            width = originalSegmentDomRect.width + this.$_segmentDragOffsetTimePx;
            if (width < this.$_minimumSegmentDurationPx) {
              width = this.$_minimumSegmentDurationPx;
            }
            break;
          case TimelineSegmentDomRectsToDragWithInfo.edgeType.left:
            {
              x = originalSegmentDomRect.x + this.$_segmentDragOffsetTimePx;
              let maximumX = originalSegmentDomRect.x + originalSegmentDomRect.width - this.$_minimumSegmentDurationPx;
              if (x > maximumX) {
                x = maximumX;
              }
              width = originalSegmentDomRect.width - (x - originalSegmentDomRect.x);
            }
            break;
          case TimelineSegmentDomRectsToDragWithInfo.edgeType.none:
            switch (endType) {
              case TimelineSegmentDomRectsToDragWithInfo.endType.bothEndFinite:
                x = originalSegmentDomRect.x + this.$_segmentDragOffsetTimePx;
                width = originalSegmentDomRect.width;
                break;
              case TimelineSegmentDomRectsToDragWithInfo.endType.rightEndInfinite:
                x = originalSegmentDomRect.x + this.$_segmentDragOffsetTimePx;
                width = originalSegmentDomRect.width - this.$_segmentDragOffsetTimePx;
                break;
              case TimelineSegmentDomRectsToDragWithInfo.endType.leftEndInfinite:
                x = originalSegmentDomRect.x;
                width = originalSegmentDomRect.width + this.$_segmentDragOffsetTimePx;
                break;
              case TimelineSegmentDomRectsToDragWithInfo.endType.bothEndInfinite:
                x = originalSegmentDomRect.x;
                width = originalSegmentDomRect.width;
                break;
            }
            break;
          }
          let timelineOverlayCanvas = this.$data.$_timelineCanvasContext;
          canvasUtils.drawRect(
            timelineOverlayCanvas,
            {
              x,
              y: originalSegmentDomRect.y,
              width,
              height: originalSegmentDomRect.height,
            },
            {
              lineWidth: 1,
              lineDash: [ 4, 4 ],
              stroke: true,
              strokeStyle: segmentGhostBorderColor.styleString,
            },
          );
        }
      }

      function drawDragArea() {
        let timelineOverlayCanvas = this.$data.$_timelineCanvasContext;
        canvasUtils.drawRect(
          timelineOverlayCanvas,
          {
            x: this.$_snappedTimelineCanvasLocalXAtMousedown,
            y: this.$_timelineCanvasLocalYAtMousedown,
            width: this.$_snappedTimelineCanvasLocalXAtMousemove - this.$_snappedTimelineCanvasLocalXAtMousedown,
            height: this.$_timelineCanvasLocalYAtMousemove - this.$_timelineCanvasLocalYAtMousedown,
          },
          {
            lineWidth: 1,
            lineDash: [ 4, 4 ],
            stroke: true,
            strokeStyle: segmentGhostBorderColor.styleString,
          },
        );
      }
    },
  }
};
</script>
